import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { css } from '@styled-system/css';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useRef } from 'react';

import { Alert, Box, Button, EntryList, Icon, Modal } from '~/components/blocks';
import { TotalAmount } from '~/components/partials';
import { SimpleLineVisitorProfile } from '~/components/partials/SimpleLineVisitorProfile';
import { LineAppointmentForChargeFragment } from '~/graphql/generated/graphql';
import { lineVisitorChargeModalState } from '~/state/reception/atoms';
import { Label } from '~/utils/label';

import { PaymentMethodBox } from '../ChargePanel/PaymentMethod/PaymentMethodBox';
import { Fields } from './types';
import { useCharge } from './use-charge';

type Props = {
  needsDeliveryMethod: boolean;
  appointment: LineAppointmentForChargeFragment;
  values: Fields;
  onBack: () => void;
};

const AmountItem = styled('dl')(
  ({ theme }) =>
    () =>
      css({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.space.m,
        '& + &': {
          borderTop: theme.borders.default,
        },
        '& > dt': {},
        '& > dd': {
          textAlign: 'right',
        },
      }),
);

export const ConfirmPanel = React.memo((props: Props) => {
  const { onBack } = props;
  const theme = useTheme();
  const resetChargeModalState = useResetAtom(lineVisitorChargeModalState);
  const { charge, charging, error } = useCharge();

  // 二重呼び出しを防ぐためのref
  const isProcessingRef = useRef(false);

  const handleCharge = useCallback(async () => {
    // 既に処理中の場合は早期リターン
    if (isProcessingRef.current || charging) {
      return;
    }

    // 処理開始をマーク
    isProcessingRef.current = true;

    try {
      await charge(props.appointment, props.values);
      resetChargeModalState();
    } catch {
      // useChargeのerrorを使うのでここでは何もしない
    } finally {
      // 処理完了をマーク
      isProcessingRef.current = false;
    }
  }, [charge, props.appointment, props.values, resetChargeModalState, charging]);

  return (
    <>
      <Modal.Body>
        {error && (
          <Alert status="error" mb={theme.space.l}>
            {error}
          </Alert>
        )}
        <SimpleLineVisitorProfile visitor={props.appointment.lineVisitor} />
        <Alert status="info" marginTop={theme.space.l}>
          <Box fontWeight={theme.fontWeights.bold} fontSize={theme.fontSizes.s}>
            {`支払い方法${props.values.isNoAmount ? '' : '・請求金額'}をご確認の上、「確定」ボタンを押してください`}
          </Box>
        </Alert>
        <EntryList marginTop={theme.space.l}>
          <EntryList.Head>支払い方法</EntryList.Head>
          <EntryList.Body>
            <PaymentMethodBox paymentMethod="cash" />
          </EntryList.Body>
        </EntryList>
        {!props.values.isNoAmount && (
          <EntryList mt={theme.space.l}>
            <EntryList.Head>請求金額</EntryList.Head>
            <EntryList.Body>
              <Box bg={theme.colors.background.bg} p={theme.space.m}>
                <AmountItem>
                  <dl>{props.values.lineItem.subject}</dl>
                  <dd>{Label.amount(+props.values.lineItem.amount || 0)}</dd>
                </AmountItem>
                {props.values.optionalLineItems.map((lineItem, idx) => (
                  <AmountItem key={idx}>
                    <dl>{lineItem.subject}</dl>
                    <dd>{Label.amount(+lineItem.amount || 0)}</dd>
                  </AmountItem>
                ))}
              </Box>
            </EntryList.Body>
          </EntryList>
        )}
        <TotalAmount lineItems={[props.values.lineItem, ...props.values.optionalLineItems]} />
      </Modal.Body>
      <Modal.Footer both>
        <Button use="white" disabled={charging} onClick={onBack}>
          <Icon icon="back-line" size="m" /> 修正
        </Button>
        <Button use="secondary" loading={charging} onClick={handleCharge}>
          確定
        </Button>
      </Modal.Footer>
    </>
  );
});

ConfirmPanel.displayName = 'ConfirmPanel';
